import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  IconButton, Box, Typography, Divider, Tooltip, Badge,
} from '@mui/material'
import {
  Home, TableChart, BarChart, CloudUpload,
  ChevronLeft, ChevronRight, Speed, Label,
  TrendingUp, Inventory2, DateRange, AdminPanelSettings,
} from '@mui/icons-material'
import { api } from '../../services/api'
import { usePlantContext } from '../../hooks/usePlantContext'

const EXPANDED_WIDTH = 240
const COLLAPSED_WIDTH = 72

// FAR-63 (Mark): only these items, top to bottom. Pages removed from the nav
// stay routable by URL; only their links are gone.
const PRIMARY_NAV = [
  { label: 'Dashboard', icon: Home, path: '/' },
  { label: 'Plans', icon: TableChart, path: '/plans' },
  { label: 'Reports', icon: BarChart, path: '/reports' },
]

const DATA_NAV = [
  // FAR-82: working days per month (the Capacity Calendar page was demo data).
  { label: 'Calendar', icon: DateRange, path: '/working-days' },
  { label: 'Demand Forecast', icon: TrendingUp, path: '/supporting/demand', source: 'forecast' },
  { label: 'Inventory', icon: Inventory2, path: '/supporting/inventory', source: 'inventory' },
  { label: 'Items', icon: Label, path: '/supporting/item-data', source: 'masterdata' },
  { label: 'Run Rates', icon: Speed, path: '/supporting/item-run-rates' },
]

// Mark: Data Hub isn't needed long term ("everything is interfaces now") but
// keep it in the bottom section for now.
const SECONDARY_NAV = [
  { label: 'Admin', icon: AdminPanelSettings, path: '/admin' },
  { label: 'Data Hub', icon: CloudUpload, path: '/data' },
]

function NavItem({ item, collapsed, active }) {
  const navigate = useNavigate()
  const button = (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <ListItemButton
        onClick={() => navigate(item.path)}
        sx={{
          minHeight: 44,
          justifyContent: collapsed ? 'center' : 'initial',
          px: 2,
          mx: 1,
          borderRadius: 2,
          bgcolor: active ? 'primary.main' : 'transparent',
          color: active ? '#FFFFFF' : 'text.secondary',
          '&:hover': { bgcolor: active ? 'primary.main' : 'action.hover' },
        }}
      >
        <ListItemIcon sx={{
          minWidth: 0,
          mr: collapsed ? 0 : 2,
          justifyContent: 'center',
          color: 'inherit',
          opacity: active ? 1 : 0.7,
        }}>
          {item.stale ? (
            <Badge variant="dot" color="error" sx={{ '& .MuiBadge-dot': { width: 8, height: 8, borderRadius: '50%' } }}>
              <item.icon fontSize="small" />
            </Badge>
          ) : (
            <item.icon fontSize="small" />
          )}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: active ? 600 : 400,
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  )

  return collapsed ? (
    <Tooltip key={item.path} title={item.label} placement="right">{button}</Tooltip>
  ) : button
}

export default function LeftNav({ collapsed, onToggle }) {
  const location = useLocation()
  const [staleItems, setStaleItems] = useState(new Set())
  const { plant } = usePlantContext()
  const width = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH

  useEffect(() => {
    // GET /data/freshness returns [{ source, status }]. This used to read it as
    // an object keyed by name with a `state` field, so the dot never lit.
    api.get(`/data/freshness?plant=${plant.plantCode}`).then(data => {
      const stale = new Set()
      for (const f of Array.isArray(data) ? data : []) {
        if (f?.status === 'stale' || f?.status === 'missing') stale.add(f.source)
      }
      setStaleItems(stale)
    }).catch(() => {})
  }, [plant.plantCode])

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }


  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          transition: 'width 200ms ease-in-out',
          overflowX: 'hidden',
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 1.5, minHeight: 56 }}>
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1, flex: 1 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '8px', bgcolor: '#003F0A',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <TableChart sx={{ color: '#FFFFFF', fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 500, fontSize: '18px', color: '#003F0A' }}>
              Planning
            </Typography>
          </Box>
        )}
        {collapsed && (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '8px', bgcolor: '#003F0A',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <TableChart sx={{ color: '#FFFFFF', fontSize: 20 }} />
            </Box>
          </Box>
        )}
        <IconButton onClick={onToggle} size="small">
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>

      <Divider />

      <List sx={{ pt: 1 }}>
        {PRIMARY_NAV.map(item => (
          <NavItem key={item.path} item={item} collapsed={collapsed} active={isActive(item.path)} />
        ))}
      </List>

      <Divider sx={{ mx: 2, my: 0.5 }} />

      <List>
        {DATA_NAV.map(item => (
          <NavItem key={item.path} item={{ ...item, stale: !!item.source && staleItems.has(item.source) }} collapsed={collapsed} active={isActive(item.path)} />
        ))}
      </List>

      <Divider sx={{ mx: 2, my: 0.5 }} />

      <List>
        {SECONDARY_NAV.map(item => (
          <NavItem
            key={item.path}
            /* the stale-data dot belongs to Data Hub only, not every secondary item */
            item={{ ...item, stale: item.path === '/data' && staleItems.size > 0 }}
            collapsed={collapsed}
            active={isActive(item.path)}
          />
        ))}
      </List>
    </Drawer>
  )
}
